import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { Accordion } from '@/components/accordions/Accordion'
import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { formatTime } from '@/utils/helpers'

import { ALL_PLAYBOOKS, formatPlaybookScope } from './AiAnswers.helpers'
import { fetchVerifiedAnswerRevisions } from './verifiedAnswers.api'

const ACTION_LABELS = { create: 'Created by', edit: 'Edited by', delete: 'Deleted by' }
const UNKNOWN_USER = 'Unknown user'
const HISTORY_FAILED = "History couldn't be loaded."
const NO_HISTORY = 'No history yet'
const NOT_FOUND = 404

/**
 * @import import { AiAnswersAnswerHistory } from '@/views/AiAnswers/AiAnswersAnswerHistory'
 * @param {object} props
 * @param {number} props.organizationId The org the answer belongs to
 * @param {number} props.answerId The verified answer whose revisions are listed
 * @param {object} props.playbooks The org's playbook options and their load state, to name scopes
 */
export const AiAnswersAnswerHistory = ({ organizationId, answerId, playbooks }) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false)
  const [revisions, setRevisions] = useState(null)
  const [failed, setFailed] = useState(false)
  const [request, setRequest] = useState(0)

  // Fetched on first open only; the modal remounts this for each edit, so it never goes stale.
  useEffect(() => {
    if (!expanded || !isNil(revisions)) {
      return undefined
    }

    let current = true
    setFailed(false)

    dispatch(fetchVerifiedAnswerRevisions({ organizationId, answerId }))
      .then((response) => {
        if (current) {
          setRevisions(response.items)
        }
      })
      .catch((error) => {
        if (!current) {
          return
        }
        // An agent-gpt without history answers 404, as does an answer deleted meanwhile.
        if (error.status === NOT_FOUND) {
          setRevisions([])
        } else {
          setFailed(true)
        }
      })

    return () => {
      current = false
    }
  }, [expanded, request])

  const loading = expanded && isNil(revisions) && !failed

  return (
    <Accordion title="History" expanded={expanded} handleToggle={() => setExpanded(!expanded)}>
      {loading && <Loader size="small" centered />}
      {failed && (
        <div className="flex-column small-gap">
          <ErrorMessage content={HISTORY_FAILED} />
          <Button small onClick={() => setRequest((value) => value + 1)}>
            Try again
          </Button>
        </div>
      )}
      {!isNil(revisions) && isEmpty(revisions) && (
        <NoData headerText={NO_HISTORY} text="Changes to this answer will be listed here." />
      )}
      {!isEmpty(revisions) && (
        <ol className="ai-answers-history">
          {revisions.map((revision) => (
            <li key={revision.version} className="ai-answers-history__item">
              <p className="muted-text">
                {`${ACTION_LABELS[revision.action]} ${revision.user_name ?? UNKNOWN_USER} · ${formatTime(revision.created_at)}`}
              </p>
              <p className="ai-answers-history__answer">{revision.answer}</p>
              <p className="muted-text">
                {isEmpty(revision.config_cids)
                  ? ALL_PLAYBOOKS
                  : formatPlaybookScope(revision.config_cids, playbooks)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Accordion>
  )
}
